/**
 * Collapse device-session rows to ONE entry per physical device (its fingerprint).
 *
 * The backend keeps a separate `Device` row per session / re-activation (spec 018 device
 * sessions), so a single physical machine legitimately shows up 2–3 times in a license or
 * console list — same fingerprint, different `device_id`, different last-seen/status. The
 * operator only wants to see one row per device. We keep the BEST row per fingerprint:
 * an `active` row beats a grace/expired one, and ties break to the most recently seen.
 *
 * Display-only — the server still tracks every session row; we just don't repeat them.
 */
interface DeviceLike {
  fingerprint: string;
  /** Present on DeviceDetail; absent on the lighter DeviceInfo (then ignored). */
  active?: boolean;
  /** Two field names exist across the API shapes — accept either. */
  last_seen_at?: string | null;
  last_seen?: string | null;
}

function lastSeenOf(d: DeviceLike): string {
  return d.last_seen_at ?? d.last_seen ?? "";
}

/** True when `a` is a "better" row to show than `b` for the same fingerprint. */
function isBetter(a: DeviceLike, b: DeviceLike): boolean {
  if (!!a.active !== !!b.active) return !!a.active; // an active session wins over grace
  return lastSeenOf(a) > lastSeenOf(b); // otherwise the most recently seen
}

/**
 * One entry per device (best row), most-recently-seen first. The dedup key defaults to the
 * fingerprint; pass `keyOf` for an org-wide list where the SAME fingerprint can legitimately
 * belong to different customers (then key by `customer + fingerprint` so they don't merge).
 */
export function dedupeDevicesByFingerprint<T extends DeviceLike>(
  devices: readonly T[],
  keyOf: (d: T) => string = (d) => d.fingerprint,
): T[] {
  const best = new Map<string, T>();
  for (const d of devices) {
    const k = keyOf(d);
    const cur = best.get(k);
    if (!cur || isBetter(d, cur)) best.set(k, d);
  }
  return Array.from(best.values()).sort((a, b) => lastSeenOf(b).localeCompare(lastSeenOf(a)));
}
